/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

/* eslint-disable no-param-reassign */
import { types } from 'mobx-state-tree';
import { getAiModelAndLanguageMapping } from '../services/aiTranslations';

const Language = types.model('Language', {
  code: types.maybeNull(types.string),
  value: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  label: types.maybeNull(types.string)
});

const AIModel = types.model('AIModel', {
  modelKey: types.maybeNull(types.string),
  title: types.maybeNull(types.string),
  languages: types.array(Language),
  glossaryService: types.optional(types.string, '')
});

export default types
  .model('AITranslationLanguageStore', {
    aiModels: types.array(AIModel),
    isLoading: types.optional(types.boolean, false),
    isLoaded: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
    refreshTrigger: types.optional(types.number, 0) // Add refresh trigger
  })
  .views(self => ({
    get allLanguages() {
      const languageMap = new Map();

      self.aiModels.forEach(model => {
        model.languages.forEach(lang => {
          const code = lang.code || lang.value;
          const name = lang.name || lang.label;
          if (code && name && !languageMap.has(code)) {
            languageMap.set(code, { code, name });
          }
        });
      });

      return Array.from(languageMap.values());
    },

    getLanguagesByModel(modelKey) {
      const model = self.aiModels.find(m => m.modelKey === modelKey);
      return model ? model.languages : [];
    },

    getLanguageName(languageCode) {
      // If data is not loaded yet, return the language code as fallback
      if (!self.isLoaded || self.aiModels.length === 0) {
        return languageCode;
      }
      const language = self.allLanguages.find(
        lang => lang.code === languageCode
      );
      return language ? language.name : languageCode;
    }
  }))
  .actions(self => ({
    async loadAIModelAndLanguageMapping() {
      // Don't reload if already loaded
      if (self.isLoaded) {
        return;
      }

      self.setLoading(true);
      self.setError(null);

      try {
        const data = await getAiModelAndLanguageMapping();
        self.setAIModelData(data);
        self.setLoaded(true);
      } catch (error) {
        self.setError(
          error.message || 'Failed to load AI model and language mapping'
        );
      } finally {
        self.setLoading(false);
      }
    },

    setLoading(loading) {
      self.isLoading = loading;
    },

    setLoaded(loaded) {
      self.isLoaded = loaded;
    },

    setError(error) {
      self.error = error;
    },

    setAIModelData(data) {
      self.aiModels.clear();
      if (Array.isArray(data)) {
        data.forEach(modelData => {
          self.aiModels.push(modelData);
        });
      }
    },

    reset() {
      self.aiModels.clear();
      self.isLoading = false;
      self.isLoaded = false;
      self.error = null;
    },

    // Trigger refresh for translation list
    triggerTranslationListRefresh() {
      self.refreshTrigger += 1;
    }
  }));
